// src/components/CategoryProductsStrip.jsx
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchRandomProductsByCategory } from '../lib/categoryStrips'
import { getCart, setCart, subscribeCart } from '../lib/cartStore'
import { slugify } from '../lib/categories'
import { formatSentenceCase } from "../utils/textFormat";

const PLACEHOLDER =
  'https://via.placeholder.com/300x300?text=Image'

export default function CategoryProductsStrip({
  title = 'Sélection',
  categoryId,
  limit = 10,
  viewAllHref = '/catalog',
}) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 👉 état local du panier (lignes)
  const [cartLines, setCartLines] = useState([])

  const trackRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // 🔔 sync panier (comme dans App.js)
  useEffect(() => {
    const initial = getCart() || []
    setCartLines(initial)

    const unsub = subscribeCart((lines) => {
      setCartLines(lines || [])
    })

    return () => {
      if (typeof unsub === 'function') unsub()
    }
  }, [])

  // 🔹 charger les produits
  useEffect(() => {
    ; (async () => {
      if (!categoryId) {
        setProducts([])
        setLoading(false)
        setError('Aucun categoryId fourni')
        return
      }

      try {
        setLoading(true)
        setError(null)
        const rows = await fetchRandomProductsByCategory(categoryId, limit)
        console.log('[CategoryProductsStrip] produits reçus :', rows.length)
        setProducts(rows)
      } catch (e) {
        console.error('[CategoryProductsStrip] error', e)
        setError(e?.message || 'Erreur de chargement des produits.')
      } finally {
        setLoading(false)
      }
    })()
  }, [categoryId, limit])

  // 🔹 qty dans le panier pour un produit donné
  const getQty = (pid) => {
    const line = cartLines.find((l) => l.pid === pid)
    return line?.qty || 0
  }

  // 🔹 modification de la quantité dans le panier
  const changeQty = (p, delta) => {
    const pid = p.id
    if (!pid) return

    const current = getCart() || []
    const idx = current.findIndex((l) => l.pid === pid)

    if (idx === -1) {
      // pas encore dans le panier → on ajoute si delta positif
      if (delta > 0) {
        const newLine = {
          pid,
          vendorId: p.vendorId || null,
          qty: 1,
        }
        setCart([...current, newLine])
      }
    } else {
      const line = current[idx]
      const nextQty = (line.qty || 0) + delta

      if (nextQty <= 0) {
        // on retire la ligne
        const filtered = current.filter((l) => l.pid !== pid)
        setCart(filtered)
      } else {
        const updated = [...current]
        updated[idx] = { ...line, qty: nextQty }
        setCart(updated)
      }
    }
    // subscribeCart mettra à jour cartLines automatiquement
  }

  // 🔹 flèches de scroll
  const updateArrows = () => {
    const el = trackRef.current
    if (!el) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 10)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 10)
  }

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    updateArrows()

    const onScroll = () => updateArrows()
    const onResize = () => updateArrows()

    el.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onResize)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [products])

  const scrollTrack = (direction) => {
    const el = trackRef.current
    if (!el) return
    const amount = el.clientWidth * 0.8
    const next =
      direction === 'left'
        ? el.scrollLeft - amount
        : el.scrollLeft + amount

    el.scrollTo({ left: next, behavior: 'smooth' })
  }

  if (!categoryId) return null

  if (loading) {
    return (
      <section className="cp-strip my-4">
        <div className="d-flex justify-content-between mb-2">
          <h5 className="mb-0">{title}</h5>
          <span className="small text-muted">Chargement…</span>
        </div>
        <div className="cp-track-wrapper">
          <div className="cp-track">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="cp-card skeleton" />
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (error || products.length === 0) {
    return (
      <section className="cp-strip my-4">
        <div className="d-flex justify-content-between mb-1">
          <h5 className="mb-0">{title}</h5>
        </div>
        <div className="small text-muted">
          {error
            ? `Impossible de charger les produits : ${error}`
            : "Aucun produit trouvé pour cette catégorie."}
        </div>
      </section>
    )
  }

  return (
    <section className="cp-strip my-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{title}</h5>
        <Link to={viewAllHref} className="small text-decoration-underline">
          Voir tout
        </Link>
      </div>

      <div className="cp-track-wrapper position-relative">
        {canScrollLeft && (
          <button
            type="button"
            className="cp-arrow cp-arrow-left"
            onClick={() => scrollTrack('left')}
            aria-label="Défiler à gauche"
          >
            ‹
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            className="cp-arrow cp-arrow-right"
            onClick={() => scrollTrack('right')}
            aria-label="Défiler à droite"
          >
            ›
          </button>
        )}

        <div className="cp-track" ref={trackRef}>
          {products.map((p) => {
            const norm = p._norm || {}
            const { img, title: t, price, oldPrice } = norm
            const slug = slugify(t || 'produit')
            const href = `/ip/${slug}/${p.id}`
            const qty = getQty(p.id)

            return (
              <Link
                key={p.id}
                to={href}
                className="cp-card card border-0 text-decoration-none h-100"
              >
                <div className="cp-img-wrapper">
                  <img
                    src={img || PLACEHOLDER}
                    alt={t || 'Produit'}
                    className="cp-img"
                    onError={(e) => {
                      e.currentTarget.src = PLACEHOLDER
                    }}
                  />
                </div>
                <div className="card-body p-2 d-flex flex-column">
                  <div className="cp-title small line-clamp-2 mb-1">
                    {formatSentenceCase(t)}                    
                  </div>
                  <div className="cp-price-row mb-1">
                    <span className="cp-price-now fw-semibold">
                      {price > 0
                        ? `${price.toLocaleString()} XOF`
                        : 'Voir le prix'}
                    </span>
                    {oldPrice > 0 && (
                      <span className="cp-price-old text-muted small ms-1 text-decoration-line-through">
                        {oldPrice.toLocaleString()} XOF
                      </span>
                    )}
                  </div>

                  {/* ⚙️ Bouton + / - comme ton snippet */}
                  <div className="mt-2">
                    {qty === 0 ? (
                      <button
                        type="button"
                        className="rail-w-add-btn"
                        onClick={(e) => {
                          e.preventDefault() // ne pas naviguer
                          changeQty(p, +1)
                        }}
                      >
                        + Ajouter
                      </button>
                    ) : (
                      <div className="qty-pill d-flex align-items-center">
                        <button
                          type="button"
                          className="qty-btn"
                          onClick={(e) => {
                            e.preventDefault()
                            changeQty(p, -1)
                          }}
                        >
                          −
                        </button>
                        <span className="qty-value">{qty}</span>
                        <button
                          type="button"
                          className="qty-btn"
                          onClick={(e) => {
                            e.preventDefault()
                            changeQty(p, +1)
                          }}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
